import { cn } from '@/lib/utils'

import { RatingBreakdown } from './RatingBreakdown'
import { Stars } from './Stars'
import { Button, ExpandButton } from '../../Theme'
import { SectionHeader } from '../../UiBlocks'
import { useReviewsActions } from '../hooks'
import { REVIEW_LABELS as LABELS } from '../lib'
import { useReviews } from '../state'

/**
 * ReviewList - Presentational component for displaying product reviews
 *
 * Pure render - all logic lives in useReviewsActions hook.
 */
export function ReviewList() {
  const { reviews, error, loading } = useReviews()
  const { averageRating, reviewCount, ratingStats, isExpanded, toggleExpand, navigateToReviewForm } =
    useReviewsActions(reviews)

  if (loading || error || !reviews || reviews.length === 0) {
    return null
  }

  return (
    <div className="relative rounded bg-white md:mb-10 mb-5 mt-5" id="productReviews">
      <SectionHeader size="base" className="tracking-wider px-10 py-3">
        {LABELS.reviews}
      </SectionHeader>

      <div
        className={cn(
          'overflow-hidden transition-all duration-300 p-4 md:p-6',
          isExpanded ? 'max-h-full' : 'max-h-[600px]'
        )}
      >
        {/* Summary */}
        <div className="border-b mb-6 flex md:flex-row flex-col items-start justify-start md:gap-8 gap-4 pb-4">
          <div className="flex flex-row items-center justify-start gap-4">
            <p className="text-custom-gray font-main-font text-5xl italic font-bold leading-none">
              {averageRating}
            </p>
            <div className="flex flex-col items-start justify-start gap-1">
              <Stars totalStars={averageRating} size="lg" />
              <p className="flex flex-row gap-1.5 items-center text-custom-gray font-main-font text-sm font-bold leading-tight">
                <span className="font-main-font text-sm font-bold">{reviewCount}</span>
                {LABELS.reviews}
              </p>
            </div>
          </div>

          <div className="flex flex-row gap-8 xl:gap-5">
            <RatingBreakdown ratingStats={ratingStats} />
            <div className="flex flex-col items-start justify-center">
              <Button
                variant="addToCart"
                size="sm"
                onClick={navigateToReviewForm}
                type="button"
              >
                {LABELS.writeReview}
              </Button>
            </div>
          </div>
        </div>

        {/* Reviews */}
        {reviews.map((review) => (
          <div className="mb-5 border-b pb-4 flex md:flex-row flex-col" key={`${review.nickname}-${review.created_at}`}>
            <div className="flex md:flex-col flex-row gap-1.5 items-start justify-start md:w-3/12 mr-3 md:mb-0 mb-2">
              <span className="font-main-font font-bold text-text-color text-sm">{review.nickname}</span>
              <span className="text-xs font-main-font font-normal text-custom-gray">{review.created_at}</span>
            </div>
            <div className="flex flex-col gap-1.5 md:w-9/12">
              <SectionHeader as="h3" size="sm">{review.summary}</SectionHeader>
              <div className="mb-2">
                {review.ratings_breakdown.map((rating) => (
                  <div className="flex flex-row items-center justify-start" key={rating.name}>
                    <Stars totalStars={Number(rating.value)} size="sm" />
                  </div>
                ))}
              </div>
              <p className="font-main-font text-sm font-normal text-text-color mb-3">{review.text}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Gradient Overlay */}
      <div
        className={cn(
          'absolute top-0 left-0 w-full h-full transition-all duration-300 bg-custom-gradient rounded pointer-events-none',
          isExpanded ? 'opacity-0' : 'opacity-100'
        )}
      />

      <ExpandButton
        isExpanded={isExpanded}
        onClick={toggleExpand}
        expandedText={LABELS.hideReviews}
        collapsedText={LABELS.showAllReviews}
        className="relative z-10"
      />
    </div>
  )
}
